import type {
	BusinessSupplier,
	EstimateLineItem,
	Price,
	PurchaseOrderLineItem,
	Supplier,
	SupplierInventory,
	SupplierInventoryClamp,
	SupplierInventoryCutoutConfiguration,
	SupplierInventoryDoorHinge,
	SupplierInventoryHandle,
	SupplierInventoryHoleConfiguration,
	VariantInventory,
	VariantInventoryLog,
} from '@prisma/client';
import {
	ClampMountType,
	CutoutType,
	DoorHingeMountType,
	DoorHingeType,
	EstimateState,
	FabricationType,
	GlassSide,
	HardwareType,
	HoleShape,
	InventoryType,
	LineOrientation,
	PricingType,
	Prisma,
	PurchaseOrderLineItemStatus,
	PurchaseOrderStatus,
	ScreenMeshType,
} from '@prisma/client';
import type { Decimal } from '@prisma/client/runtime/library';
import { nullableNumber } from '@utils/reactHookForm';
import { CUSTOM_FORM, isNotNullish } from '@utils/types';
import { nonEmptyArray } from '@utils/zodHelpers';
import { z } from 'zod';

export type EditSupplierInventoryRow = z.infer<typeof SupplierInventorySchema.editSupplierInventorySchema>;

export type SupplierInventoryFabricationsSubFormType = Pick<
	z.infer<typeof SupplierInventorySchema.baseSupplierInventorySchema>,
	'hardwareType' | 'supplierInventoryClamp' | 'supplierInventoryDoorHinge' | 'supplierInventoryHandle'
>;

export type PriceFull = {
	id: number;
	supplierInventoryId: number;
	businessSupplierId: number;
	name: string;
	nameWithSupplier: string;
	isLowest?: boolean;
	supplier: string;
	variant: string | null;
	priceType: PricingType;
	price: number;
	vendorPrice?: number;
	sellPrice?: number;
	priceLabel: string;
	surchargePercent: number;
	minimumSquareFeet: number | null;
	variantInventories?: VariantInventory[];
};

export type PriceWithVariantInventories = Price & { variantInventories: (VariantInventory & { variantInventoryLogs: VariantInventoryLog[] })[] };

export type ISupplierInventoryPriced = Omit<
	SupplierInventory,
	'createdAt' | 'deletedAt' | 'effectiveEndDate' | 'effectiveStartDate' | 'updatedAt' | 'temperingType'
> & {
	businessSupplier?: BusinessSupplier & { supplier: Supplier };
	prices: (Price & {
		estimateLineItems?: EstimateLineItem[];
		purchaseOrderLineItems?: PurchaseOrderLineItem[];
		variantInventories?: (VariantInventory & { variantInventoryLogs: VariantInventoryLog[] })[];
	})[];
};

export interface ICutoutConfigurationForTable
	extends Pick<
		SupplierInventoryCutoutConfiguration,
		| 'heightFraction'
		| 'mouseEarDistanceCenterToCenterFraction'
		| 'mouseEarDistanceCenterToCutSideFraction'
		| 'mouseEarRadiusFraction'
		| 'rectangleCornerRadiusFraction'
		| 'type'
		| 'widthFraction'
	> {
	height: Decimal | number;
	id?: number | undefined;
	mouseEarDistanceCenterToCenter: Decimal | number | null;
	mouseEarDistanceCenterToCutSide: Decimal | number | null;
	mouseEarRadius: Decimal | number | null;
	rectangleCornerRadius: Decimal | number | null;
	width: Decimal | number;
}

export interface IHoleConfigurationForTable
	extends Pick<
		SupplierInventoryHoleConfiguration,
		| 'closestHorizontalSide'
		| 'closestVerticalSide'
		| 'distanceToHorizontalSideFraction'
		| 'distanceToVerticalSideFraction'
		| 'heightFraction'
		| 'shape'
		| 'widthFraction'
	> {
	distanceToHorizontalSide: Decimal | number;
	distanceToVerticalSide: Decimal | number;
	height: Decimal | number | null;
	id?: number | undefined;
	width: Decimal | number;
}

export interface ISupplierInventoryForTable extends ISupplierInventoryPriced {
	supplierInventoryClamp:
		| (Pick<SupplierInventoryClamp, 'cornerRadiusFraction' | 'gapFraction' | 'heightFraction' | 'mountType' | 'widthFraction'> & {
				adjacentPanelCutoutConfigurationOptions: ICutoutConfigurationForTable[];
				adjacentPanelHoleConfigurations: IHoleConfigurationForTable[];
				cornerRadius: Decimal | number | null;
				gap: Decimal | number | null;
				height: Decimal | number;
				id?: number | undefined;
				primaryPanelCutoutConfigurationOptions: ICutoutConfigurationForTable[];
				primaryPanelHoleConfigurations: IHoleConfigurationForTable[];
				width: Decimal | number;
		  })
		| null
		| undefined;
	supplierInventoryDoorHinge:
		| (Pick<
				SupplierInventoryDoorHinge,
				| 'bottomGapFraction'
				| 'cornerRadiusFraction'
				| 'heightFraction'
				| 'hingeSideGapFraction'
				| 'mountType'
				| 'topGapFraction'
				| 'type'
				| 'widthFraction'
				| 'maxGlassThicknessFraction'
				| 'minGlassThicknessFraction'
		  > & {
				bottomGap: Decimal | number | null;
				cornerRadius: Decimal | number | null;
				doorCutoutConfigurationOptions: ICutoutConfigurationForTable[];
				height: Decimal | number;
				hingeSideGap: Decimal | number | null;
				id?: number | undefined;
				maxGlassThickness: Decimal | number;
				minGlassThickness: Decimal | number;
				panelCutoutConfigurationOptions: ICutoutConfigurationForTable[];
				topGap: Decimal | number | null;
				width: Decimal | number;
		  })
		| null
		| undefined;
	supplierInventoryHandle:
		| (Pick<SupplierInventoryHandle, 'cornerRadiusFraction' | 'lengthFraction' | 'orientation' | 'widthFraction'> & {
				cornerRadius: Decimal | number | null;
				length: Decimal | number;
				holeConfigurations: IHoleConfigurationForTable[];
				id?: number | undefined;
				width: Decimal | number;
		  })
		| null
		| undefined;
}

export type ISupplierInventoryForForm = Omit<ISupplierInventoryForTable, 'businessSupplierId'> & {
	businessSupplierId?: number | undefined;
};

export enum InchOptions {
	sixteenth = '1/16',
	eighth = '1/8',
	quarter = '1/4',
	threeSixteenth = '3/16',
	fiveSixteenth = '5/16',
	threeEighth = '3/8',
	sevenSixteenth = '7/16',
	half = '1/2',
	nineSixteenth = '9/16',
	fiveEighth = '5/8',
	elevenSixteenth = '11/16',
	threeQuarter = '3/4',
	thirteenSixteenth = '13/16',
	sevenEighth = '7/8',
	fifteenSixteenth = '15/16',

	// 32nds ughhh
	one32 = '1/32',
	three32 = '3/32',
	five32 = '5/32',
	seven32 = '7/32',
	nine32 = '9/32',
	eleven32 = '11/32',
	thirteen32 = '13/32',
	fifteen32 = '15/32',
	seventeen32 = '17/32',
	nineteen32 = '19/32',
	twenty132 = '21/32',
	twenty332 = '23/32',
	twenty532 = '25/32',
	twenty732 = '27/32',
	twenty932 = '29/32',
	thirtyOne32 = '31/32',
}

export const glassTypes: InventoryType[] = [
	InventoryType.INSULATED_GLASS,
	InventoryType.LAMINATED_GLASS,
	InventoryType.LEXAN,
	InventoryType.MIRROR,
	InventoryType.PATTERN_GLASS,
	InventoryType.PLEXI,
	InventoryType.SINGLE_LITE_GLASS,
	InventoryType.WINDOW,
];

export const temperedGlassTypes: InventoryType[] = [
	InventoryType.INSULATED_GLASS,
	InventoryType.LAMINATED_GLASS,
	InventoryType.PATTERN_GLASS,
	InventoryType.SINGLE_LITE_GLASS,
];

export const nonTemperedGlassWithMinimumSquareFeet: InventoryType[] = [
	InventoryType.LEXAN,
	InventoryType.MIRROR,
	InventoryType.PLEXI,
	InventoryType.SCREENS,
	InventoryType.WINDOW, // NB: window glass can be tempered, but we're not modeling that rn
];

export const inventoryTypesWithThickness1: InventoryType[] = [
	InventoryType.GLASS_FABRICATION,
	InventoryType.LAMINATED_GLASS,
	InventoryType.LEXAN,
	InventoryType.MIRROR,
	InventoryType.PATTERN_GLASS,
	InventoryType.PLEXI,
	InventoryType.SINGLE_LITE_GLASS,
];

export const inventoryTypesWithGlassType1: InventoryType[] = [
	InventoryType.INSULATED_GLASS,
	InventoryType.LAMINATED_GLASS,
	InventoryType.MIRROR,
	InventoryType.PATTERN_GLASS,
	InventoryType.SINGLE_LITE_GLASS,
];

export type SupplierInventoryDetailsFormType = z.infer<typeof SupplierInventorySchema.updateSupplierInventoryWithPricesSchema>;

export class SupplierInventorySchema {
	static defaultSelect = Prisma.validator<Prisma.SupplierInventorySelect>()({
		id: true,
		name: true,
		inventoryType: true,
		businessSupplierId: true,
		businessSupplier: {
			select: {
				id: true,
				surchargePercent: true,
				supplier: {
					select: {
						id: true,
						name: true,
						type: true,
					},
				},
			},
		},
		prices: {
			select: {
				id: true,
				price: true,
				variant: true,
				sellPrice: true,
			},
		},
		priceType: true,
		type: true,
		hardwareType: true,
		pane2Type: true,
		thickness: true,
		pane2Thickness: true,
		overallThickness: true,
		code: true,
		color: true,
		hasOverrides: true,
		minimumSquareFeet: true,
		screenMeshType: true,
		fabricationType: true,
		costCodeId: true,
	});

	static selectForTable = Prisma.validator<Prisma.SupplierInventorySelect>()({
		businessSupplier: {
			include: {
				supplier: true,
			},
		},
		prices: {
			where: { deletedAt: null },
			orderBy: { id: 'asc' },
			include: {
				estimateLineItems: {
					where: {
						deletedAt: null,
						isPullingFromInventory: true,
						isPickedFromInventory: false,
						estimateGroup: {
							deletedAt: null,
							estimate: { deletedAt: null, estimateState: EstimateState.WON },
						},
					},
				},
				purchaseOrderLineItems: {
					where: {
						deletedAt: null,
						status: { notIn: [PurchaseOrderLineItemStatus.ACCEPTED, PurchaseOrderLineItemStatus.NOT_ACCEPTED] },
						purchaseOrder: {
							status: {
								in: [PurchaseOrderStatus.AWAITING_DELIVERY, PurchaseOrderStatus.ARRIVED, PurchaseOrderStatus.PARTIALLY_ACCEPTED],
							},
							deletedAt: null,
						},
					},
				},
				variantInventories: {
					where: { deletedAt: null },
					include: { variantInventoryLogs: { where: { deletedAt: null }, orderBy: { id: 'desc' } } },
				},
			},
		},
		supplierInventoryClamp: {
			include: {
				adjacentPanelCutoutConfigurationOptions: { where: { deletedAt: null } },
				adjacentPanelHoleConfigurations: { where: { deletedAt: null } },
				primaryPanelCutoutConfigurationOptions: { where: { deletedAt: null } },
				primaryPanelHoleConfigurations: { where: { deletedAt: null } },
			},
			where: { deletedAt: null },
		},
		supplierInventoryDoorHinge: {
			include: {
				doorCutoutConfigurationOptions: { where: { deletedAt: null } },
				panelCutoutConfigurationOptions: { where: { deletedAt: null } },
			},
			where: { deletedAt: null },
		},
		supplierInventoryHandle: {
			include: { holeConfigurations: { where: { deletedAt: null } } },
			where: { deletedAt: null },
		},
	});

	static selectForTableWithDeletedSubItems = Prisma.validator<Prisma.SupplierInventorySelect>()({
		businessSupplier: {
			include: {
				supplier: true,
			},
		},
		prices: {
			where: { deletedAt: null },
			orderBy: { id: 'asc' },
			include: {
				estimateLineItems: {
					where: {
						deletedAt: null,
						isPullingFromInventory: true,
						isPickedFromInventory: false,
						estimateGroup: {
							deletedAt: null,
							estimate: { deletedAt: null, estimateState: EstimateState.WON },
						},
					},
				},
				purchaseOrderLineItems: {
					where: {
						deletedAt: null,
						status: { notIn: [PurchaseOrderLineItemStatus.ACCEPTED, PurchaseOrderLineItemStatus.NOT_ACCEPTED] },
						purchaseOrder: {
							status: {
								in: [PurchaseOrderStatus.AWAITING_DELIVERY, PurchaseOrderStatus.ARRIVED, PurchaseOrderStatus.PARTIALLY_ACCEPTED],
							},
							deletedAt: null,
						},
					},
				},
				variantInventories: {
					where: { deletedAt: null },
					include: { variantInventoryLogs: { where: { deletedAt: null }, orderBy: { id: 'desc' } } },
				},
			},
		},
		supplierInventoryClamp: {
			include: {
				adjacentPanelCutoutConfigurationOptions: { where: { deletedAt: null } },
				adjacentPanelHoleConfigurations: { where: { deletedAt: null } },
				primaryPanelCutoutConfigurationOptions: { where: { deletedAt: null } },
				primaryPanelHoleConfigurations: { where: { deletedAt: null } },
			},
		},
		supplierInventoryDoorHinge: {
			include: {
				doorCutoutConfigurationOptions: { where: { deletedAt: null } },
				panelCutoutConfigurationOptions: { where: { deletedAt: null } },
			},
		},
		supplierInventoryHandle: {
			include: { holeConfigurations: { where: { deletedAt: null } } },
		},
	});

	static priceDefaultSelect = Prisma.validator<Prisma.PriceSelect>()({
		id: true,
		variant: true,
		price: true,
		supplierInventoryId: true,
		createdAt: true,
		updatedAt: true,
		deletedAt: true,
	});

	static cutoutConfigurationSchema = z.object({
		deleteMe: z.boolean().optional(),
		height: z.number(),
		heightFraction: z.string().min(1),
		id: z.number().optional(),
		mouseEarDistanceCenterToCenter: z.number().nullable(),
		mouseEarDistanceCenterToCenterFraction: z.string().nullable(),
		mouseEarDistanceCenterToCutSide: z.number().nullable(),
		mouseEarDistanceCenterToCutSideFraction: z.string().nullable(),
		mouseEarRadius: z.number().nullable(),
		mouseEarRadiusFraction: z.string().nullable(),
		rectangleCornerRadius: z.number().nullable(),
		rectangleCornerRadiusFraction: z.string().nullable(),
		type: z.enum(CutoutType),
		width: z.number(),
		widthFraction: z.string().min(1),
	});

	static holeConfigurationSchema = z.object({
		deleteMe: z.boolean().optional(),
		closestHorizontalSide: z.enum(GlassSide),
		closestVerticalSide: z.enum(GlassSide),
		distanceToHorizontalSide: z.number(),
		distanceToHorizontalSideFraction: z.string().min(1),
		distanceToVerticalSide: z.number(),
		distanceToVerticalSideFraction: z.string().min(1),
		height: z.number().nullable(),
		heightFraction: z.string().nullable(),
		id: z.number().optional(),
		shape: z.enum(HoleShape),
		width: z.number(),
		widthFraction: z.string().min(1),
	});

	static supplierInventoryClampSchema = z
		.object({
			adjacentPanelCutoutConfigurationOptions: z.array(this.cutoutConfigurationSchema),
			adjacentPanelHoleConfigurations: z.array(this.holeConfigurationSchema),
			cornerRadius: z.number().nullable(),
			cornerRadiusFraction: z.string().nullable(),
			gap: z.number().nullable(),
			gapFraction: z.string().nullable(),
			height: z.number(),
			heightFraction: z.string().min(1),
			id: z.number().optional(),
			mountType: z.enum(ClampMountType),
			primaryPanelCutoutConfigurationOptions: z.array(this.cutoutConfigurationSchema),
			primaryPanelHoleConfigurations: z.array(this.holeConfigurationSchema),
			width: z.number(),
			widthFraction: z.string().min(1),
		})
		.nullish();

	static supplierInventoryDoorHingeSchema = z
		.object({
			bottomGap: z.number().nullable(),
			bottomGapFraction: z.string().nullable(),
			cornerRadius: z.number().nullable(),
			cornerRadiusFraction: z.string().nullable(),
			doorCutoutConfigurationOptions: z.array(this.cutoutConfigurationSchema),
			height: z.number(),
			heightFraction: z.string().min(1),
			hingeSideGap: z.number().nullable(),
			hingeSideGapFraction: z.string().nullable(),
			id: z.number().optional(),
			mountType: z.enum(DoorHingeMountType),
			maxGlassThickness: z.number(),
			maxGlassThicknessFraction: z.string().min(1),
			minGlassThickness: z.number(),
			minGlassThicknessFraction: z.string().min(1),
			panelCutoutConfigurationOptions: z.array(this.cutoutConfigurationSchema),
			topGap: z.number().nullable(),
			topGapFraction: z.string().nullable(),
			type: z.enum(DoorHingeType),
			width: z.number(),
			widthFraction: z.string().min(1),
		})
		.nullish();

	static supplierInventoryHandleSchema = z
		.object({
			cornerRadius: z.number().nullable(),
			cornerRadiusFraction: z.string().nullable(),
			length: z.number(),
			lengthFraction: z.string().min(1),
			holeConfigurations: z.array(this.holeConfigurationSchema),
			id: z.number().optional(),
			orientation: z.enum(LineOrientation),
			width: z.number(),
			widthFraction: z.string(),
		})
		.nullish();

	static baseSupplierInventorySchema = z.object({
		code: z.string().nullish(),
		costCodeId: z.number().nullish(),
		customType: z.string().optional().nullable(),
		customPane2Type: z.string().optional().nullable(),
		fabricationType: z.enum(FabricationType).optional().nullable(),
		hardwareType: z.enum(HardwareType).nullish(),
		inventoryType: z.enum(InventoryType),
		minimumSquareFeet: z.string().optional().nullable(),
		name: z.string().min(1, {
			error: 'Entering a name is required.',
		}),
		pane2Thickness: z.string().optional().nullable(),
		pane2Type: z.string().optional().nullable(),
		price: z.number().optional().nullable(),
		priceType: z.enum(PricingType),
		screenMeshType: z.enum(ScreenMeshType).optional().nullable(),
		sellPrice: z.number().optional().nullable(),
		variant: z.string().optional().nullable(),
		supplierInventoryClamp: this.supplierInventoryClampSchema,
		supplierInventoryDoorHinge: this.supplierInventoryDoorHingeSchema,
		supplierInventoryHandle: this.supplierInventoryHandleSchema,
		thickness: z.string().optional().nullable(),
		type: z.string().optional().nullable(),
	});

	static createSupplierInventorySchema = this.baseSupplierInventorySchema.extend({
		businessSupplierId: z.number(),
	});

	static upsertPriceSchema = z
		.object({
			/// This is the supplier inventory id
			id: z.number().nullish(),
			price: z.number().nullable(),
			priceId: z.number().nullish(),
			sellPrice: nullableNumber,
			variant: z.string().nullable(),
			quantity: z.number().nullish(),
		})
		.refine((data) => isNotNullish(data.price), {
			path: ['price'],
			error: 'Price is required.',
		});

	static updateSupplierInventoryWithPricesSchema = z
		.object({
			id: z.number(),

			name: z.string().min(3),
			code: z.string().nullish(),
			costCodeId: z.number().nullish(),
			inventoryType: z.enum(InventoryType),
			businessSupplierId: z.number(),
			priceType: z.enum(PricingType),
			minimumSquareFeet: nullableNumber,

			thickness: z.string().nullish(),
			pane2Thickness: z.string().nullish(),
			type: z.string().nullish(),
			pane2Type: z.string().nullish(),

			createCustomType1: z.boolean(),
			createCustomType2: z.boolean(),

			customType: z.string().nullish(),
			customPane2Type: z.string().nullish(),

			fabricationType: z.enum(FabricationType).nullable(),
			screenMeshType: z.enum(ScreenMeshType).nullable(),
			hardwareType: z.enum(HardwareType).nullable(),

			supplierInventoryClamp: this.supplierInventoryClampSchema,
			supplierInventoryDoorHinge: this.supplierInventoryDoorHingeSchema,
			supplierInventoryHandle: this.supplierInventoryHandleSchema,

			prices: nonEmptyArray(
				z.object({
					id: z.number().optional(),
					lowQuantityThreshold: nullableNumber,
					minimumSquareFeet: nullableNumber,
					onHand: nullableNumber,
					price: z.number().min(0),
					sellPrice: nullableNumber,
					variant: z.string().nullish(),
				})
			),
		})
		.superRefine((values, ctx) => {
			if (values.inventoryType === InventoryType.INSULATED_GLASS && !values.createCustomType2 && !values.pane2Type) {
				ctx.addIssue({
					error: 'Required',
					path: ['pane2Type'],
					code: 'custom',
				});
			}

			if (inventoryTypesWithGlassType1.includes(values.inventoryType) && !values.createCustomType1 && !values.type) {
				return ctx.addIssue({
					error: 'Required',
					path: ['type'],
					code: 'custom',
				});
			}

			if (values.createCustomType1 && !values.customType) {
				return ctx.addIssue({
					error: 'Required',
					path: ['customType'],
					code: 'custom',
				});
			}

			if (values.createCustomType2 && !values.customPane2Type) {
				return ctx.addIssue({
					error: 'Required',
					path: ['customPane2Type'],
					code: 'custom',
				});
			}

			if (values.inventoryType === InventoryType.HARDWARE && values.hardwareType) {
				switch (values.hardwareType) {
					case HardwareType.CLAMP:
						if (!values.supplierInventoryClamp) {
							return ctx.addIssue({
								error: 'Clamp details required',
								path: ['hardwareType'],
								code: 'custom',
							});
						}
						break;
					case HardwareType.DOOR_HINGE:
						if (!values.supplierInventoryDoorHinge) {
							return ctx.addIssue({
								error: 'Door hinge details required',
								path: ['hardwareType'],
								code: 'custom',
							});
						}
						break;
					case HardwareType.HANDLE:
						if (!values.supplierInventoryHandle) {
							return ctx.addIssue({
								error: 'Handle details required',
								path: ['hardwareType'],
								code: 'custom',
							});
						}
						break;
					default:
						break;
				}
			}
		});

	static editSupplierInventorySchema = this.baseSupplierInventorySchema
		.extend({
			annealedPriceId: z.number().optional().nullable(),
			id: z.number(),
			priceId: z.number().optional().nullable(),
			temperedPriceId: z.number().optional().nullable(),
		})
		.superRefine((values, ctx) => {
			if (values.inventoryType === InventoryType.INSULATED_GLASS && !values.pane2Type) {
				ctx.addIssue({
					error: 'Required.',
					path: ['pane2Type'],
					code: 'custom',
				});
			}

			if (inventoryTypesWithGlassType1.includes(values.inventoryType) && !values.type) {
				return ctx.addIssue({
					error: 'Required.',
					path: ['type'],
					code: 'custom',
				});
			}

			if (values.type === CUSTOM_FORM.ADD_OWN_TYPE && !values.customType) {
				return ctx.addIssue({
					error: 'Required.',
					path: ['customType'],
					code: 'custom',
				});
			}

			if (values.pane2Type === CUSTOM_FORM.ADD_OWN_TYPE && !values.customPane2Type) {
				return ctx.addIssue({
					error: 'Required.',
					path: ['customPane2Type'],
					code: 'custom',
				});
			}
		});

	static inventoryByBusinessSupplierInput = z.object({
		businessSupplierId: z.number(),
		category: z.enum(InventoryType),
		hardwareType: z.enum(HardwareType).nullish(),
		search: z.string().trim().optional(),
	});
}
